/** @format */

import { Container, styled, Box, Link, Typography } from "@mui/material";
import React from "react";
import { Footer } from "../allComponents/AllComponents";

const PageContainer = styled(Container)(({ theme }) => ({
	paddingTop: theme.spacing(8),
	paddingBottom: theme.spacing(8),
	fontFamily: "'Inter', sans-serif",
}));

const Section = styled(Box)(({ theme }) => ({
	marginBottom: theme.spacing(4),
}));

const TermsOfService = () => {
	return (
		<React.Fragment>
			<PageContainer maxWidth="md">
				<Typography
					variant="h3"
					gutterBottom
					sx={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 600 }}>
					Terms of Service
				</Typography>
				<Typography variant="subtitle2" gutterBottom>
					Last updated: June 5, 2026
				</Typography>
				<Section>
					<Typography variant="h5" gutterBottom>
						1. Introduction
					</Typography>
					<Typography variant="body1" paragraph>
						Welcome to FundsyFi. These Terms of Service (“Terms”) govern your
						access to and use of the FundsyFi platform (the “Service”),
						including both of our products: <b>FundsyFi Circle</b>, our group
						savings and rotating-contribution platform for individuals, and{" "}
						<b>FundsyFi Recover</b>, our AI-assisted accounts-receivable and
						revenue-recovery platform for businesses that connects to accounting
						systems such as QuickBooks Online. Some sections apply only to a
						specific product, as noted. By creating an account or using the
						Service, you agree to be bound by these Terms and our{" "}
						<Link href="/privacy-policy">Privacy Policy</Link>. If you do not
						agree, please do not use the Service.
					</Typography>
				</Section>
				<Section>
					<Typography variant="h5" gutterBottom>
						2. Definitions
					</Typography>
					<Box component="ul" sx={{ pl: 2 }}>
						<li>
							<Typography component="span" variant="body1">
								<b>“FundsyFi Circle”</b> means our group savings and
								rotating-contribution product for individuals.
							</Typography>
						</li>
						<li>
							<Typography component="span" variant="body1">
								<b>“FundsyFi Recover”</b> means our accounts-receivable and
								revenue-recovery product for businesses, including QuickBooks
								Online integration, automated invoice reminders and
								communications, and related tools.
							</Typography>
						</li>
						<li>
							<Typography component="span" variant="body1">
								<b>“Circle”</b> means a group of Users who agree to contribute a
								set amount on a recurring schedule and receive a rotating
								payout.
							</Typography>
						</li>
						<li>
							<Typography component="span" variant="body1">
								<b>“Contribution”</b> means the amount each Circle member pays
								into the pot at each interval.
							</Typography>
						</li>
						<li>
							<Typography component="span" variant="body1">
								<b>“User,” “you,” or “your”</b> refers to anyone who registers
								for or uses the Service.
							</Typography>
						</li>
					</Box>
				</Section>
				<Section>
					<Typography variant="h5" gutterBottom>
						3. Account Registration
					</Typography>
					<Box component="ol" sx={{ pl: 2 }}>
						<li>
							<Typography variant="body1">
								You must be at least 18 years old to register.
							</Typography>
						</li>
						<li>
							<Typography variant="body1">
								Provide accurate, complete information and keep it up-to-date.
							</Typography>
						</li>
						<li>
							<Typography variant="body1">
								You are responsible for all activity under your account
								credentials.
							</Typography>
						</li>
					</Box>
				</Section>
				<Section>
					<Typography variant="h5" gutterBottom>
						4. Fees & Payment Processing
					</Typography>
					<Typography variant="body1" paragraph>
						<b>FundsyFi is committed to transparency.</b> Here are all the fees
						you may encounter:
					</Typography>

					<Typography variant="h6" gutterBottom sx={{ mt: 2, fontWeight: 600 }}>
						Contribution Processing Fees
					</Typography>
					<Box component="ul" sx={{ pl: 2, mb: 2 }}>
						<li>
							<Typography variant="body1">
								<b>Stripe Processing Fee:</b> 2.9% + $0.30 per contribution
								(charged by Stripe, not FundsyFi)
							</Typography>
						</li>
						<li>
							<Typography variant="body1">
								<b>FundsyFi Platform Fee:</b> $0 on contributions
							</Typography>
						</li>
						<li>
							<Typography variant="body1">
								The Stripe fee is charged on top of your contribution amount.
								Your full contribution goes into the circle pool.
							</Typography>
						</li>
					</Box>

					<Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
						Payout Processing Fees
					</Typography>
					<Box component="ul" sx={{ pl: 2, mb: 2 }}>
						<li>
							<Typography variant="body1">
								<b>Standard Payout (2-3 days):</b> FREE from Stripe
							</Typography>
						</li>
						<li>
							<Typography variant="body1">
								<b>Instant Payout (minutes):</b> 1% (min $0.50) charged by
								Stripe
							</Typography>
						</li>
						<li>
							<Typography variant="body1">
								<b>FundsyFi Platform Fee:</b> 2.5% deducted from your payout
								amount
							</Typography>
						</li>
					</Box>

					<Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
						Late Contribution Fees
					</Typography>
					<Box component="ul" sx={{ pl: 2, mb: 2 }}>
						<li>
							<Typography variant="body1">
								<b>Grace Period:</b> 24 hours to pay without any late fee
							</Typography>
						</li>
						<li>
							<Typography variant="body1">
								<b>Late Fee:</b> 2.5% per overdue contribution (only applies
								after grace period)
							</Typography>
						</li>
					</Box>

					<Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
						Fee Examples
					</Typography>
					<Box
						sx={{ backgroundColor: "#f5f5f5", p: 2, borderRadius: 1, mb: 2 }}>
						<Typography variant="body2" paragraph>
							<b>Example 1 - $100 Contribution:</b>
							<br />
							Your contribution: $100.00
							<br />
							Stripe fee (2.9% + $0.30): +$3.20
							<br />
							You pay: $103.20
							<br />
							Amount added to circle pool: $100.00
						</Typography>
						<Typography variant="body2">
							<b>Example 2 - $800 Payout (8 members × $100):</b>
							<br />
							Pool total: $800.00
							<br />
							FundsyFi fee (2.5%): −$20.00
							<br />
							Stripe payout fee (standard): $0.00
							<br />
							You receive: $780.00
						</Typography>
					</Box>

					<Typography variant="body1" paragraph>
						<b>Full Transparency:</b> All fees are clearly displayed before you
						confirm any transaction. You'll always see the exact amount being
						contributed to your circle and the exact amount you'll receive in
						your bank account.
					</Typography>
				</Section>{" "}
				<Section>
					<Typography variant="h5" gutterBottom>
						5. Circle & Contribution Terms
					</Typography>
					<Box component="ol" sx={{ pl: 2 }}>
						<li>
							<Typography variant="body1">
								You create or join a Circle by specifying: Circle name,
								Contribution amount, Schedule (weekly, biweekly, etc.)
							</Typography>
						</li>
						<li>
							<Typography variant="body1">
								All Contributions are automatically collected and held in escrow
								by Stripe.
							</Typography>
						</li>
						<li>
							<Typography variant="body1">
								Payouts rotate: on your scheduled turn, you receive the total
								pot.
							</Typography>
						</li>
						<li>
							<Typography variant="body1">
								If any member misses a Contribution, automated reminders will be
								sent; repeated failures may result in Circle removal.
							</Typography>
						</li>
					</Box>
				</Section>
				<Section>
					<Typography variant="h5" gutterBottom>
						6. Payment Processing & Security
					</Typography>
					<Typography variant="body1" paragraph>
						We use Stripe Connect to process payments and hold funds in escrow.
						Your payment data is never stored on our servers. We comply with
						PCI-DSS standards and industry best practices.
					</Typography>
				</Section>
				<Section>
					<Typography variant="h5" gutterBottom>
						7. Cancellations & Refunds
					</Typography>
					<Typography variant="body1" paragraph>
						You may cancel your participation in a circle at any time before a
						scheduled payout. Subscription fees are not applicable. For payout
						disputes, contact{" "}
						<Link href="mailto:support@fundsyfi.com">support@fundsyfi.com</Link>{" "}
						within 30 days.
					</Typography>
				</Section>
				<Section>
					<Typography variant="h5" gutterBottom>
						8. User Conduct
					</Typography>
					<Typography variant="body1" paragraph>
						You agree not to use the Service for fraudulent or unlawful
						purposes, harass other Users, or interfere with or disrupt the
						Service.
					</Typography>
				</Section>
				<Section>
					<Typography variant="h5" gutterBottom>
						9. Intellectual Property
					</Typography>
					<Typography variant="body1" paragraph>
						All FundsyFi branding, logos, text, code, and designs are our
						exclusive property. You may not reproduce, distribute, or create
						derivative works without our prior written consent.
					</Typography>
				</Section>
				<Section>
					<Typography variant="h5" gutterBottom>
						10. Privacy
					</Typography>
					<Typography variant="body1" paragraph>
						Use of personal data is governed by our{" "}
						<Link href="/privacy-policy">Privacy Policy</Link>.
					</Typography>
				</Section>
				<Section>
					<Typography variant="h5" gutterBottom>
						11. Disclaimers
					</Typography>
					<Typography variant="body1" paragraph>
						THE SERVICE IS PROVIDED “AS IS” WITHOUT WARRANTIES OF ANY KIND. WE
						DISCLAIM ALL IMPLIED WARRANTIES, INCLUDING MERCHANTABILITY, FITNESS
						FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
					</Typography>
				</Section>
				<Section>
					<Typography variant="h5" gutterBottom>
						12. Limitation of Liability
					</Typography>
					<Typography variant="body1" paragraph>
						TO THE MAXIMUM EXTENT PERMITTED BY LAW, FUND SYFI AND ITS AFFILIATES
						WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, OR
						CONSEQUENTIAL DAMAGES ARISING OUT OF YOUR USE OF THE SERVICE.
					</Typography>
				</Section>
				<Section>
					<Typography variant="h5" gutterBottom>
						13. Indemnification
					</Typography>
					<Typography variant="body1" paragraph>
						You agree to indemnify and hold FundsyFi, its officers, employees,
						and agents harmless from any claims, losses, liabilities, damages,
						costs, or expenses arising from your breach of these Terms.
					</Typography>
				</Section>
				<Section>
					<Typography variant="h5" gutterBottom>
						14. Governing Law
					</Typography>
					<Typography variant="body1" paragraph>
						These Terms shall be governed by and construed in accordance with
						the laws of the State of Texas, without regard to conflict-of-law
						principles.
					</Typography>
				</Section>
				<Section>
					<Typography variant="h5" gutterBottom>
						15. Changes to Terms
					</Typography>
					<Typography variant="body1" paragraph>
						We may modify these Terms at any time. If we make material changes,
						we will notify you by email or in-app. Your continued use after
						changes are posted constitutes acceptance.
					</Typography>
				</Section>
				<Section>
					<Typography variant="h5" gutterBottom>
						16. SMS / Text Messaging Program
					</Typography>
					<Typography variant="body1" paragraph>
						<b>Program name:</b> FundsyFi Payment Reminders. If you are a
						customer of a business that uses FundsyFi, and you provide your
						mobile number to that business or add it on our sign-up form, you
						consent to receive transactional text messages from FundsyFi on
						that business's behalf, including invoice due and past-due
						reminders, payment confirmations, and related account
						notifications. We do not send marketing or promotional text
						messages, and we never share your mobile information with third
						parties or affiliates for their own marketing purposes.
					</Typography>
					<Typography variant="body1" paragraph>
						<b>Message frequency varies</b> based on your outstanding invoices
						and account activity. <b>Message and data rates may apply.</b>
					</Typography>
					<Typography variant="body1" paragraph>
						You can cancel the SMS service at any time by replying{" "}
						<b>STOP</b> to any message. After you send <b>STOP</b>, we will
						stop sending text messages to that number; you may receive one
						final message confirming your opt-out. To opt back in, reply{" "}
						<b>START</b>. For help at any time, reply <b>HELP</b> or contact us
						at{" "}
						<Link href="mailto:support@fundsyfi.com">support@fundsyfi.com</Link>.
						Carriers are not liable for delayed or undelivered messages.
					</Typography>
				</Section>
				<Section>
					<Typography variant="h5" gutterBottom>
						17. FundsyFi Recover (Business Users)
					</Typography>
					<Typography variant="body1" paragraph>
						The following terms apply to business users of FundsyFi Recover, in
						addition to the general Terms above.
					</Typography>
					<Box component="ul" sx={{ pl: 2 }}>
						<li>
							<Typography variant="body1">
								<b>Subscription.</b> FundsyFi Recover is provided on a
								subscription basis (including any free trial). Billing terms,
								pricing, and renewals are presented in-app at sign-up.
							</Typography>
						</li>
						<li>
							<Typography variant="body1">
								<b>Accounting connection.</b> You authorize FundsyFi to connect
								to your QuickBooks Online account and to synchronize customers,
								invoices, payments, and related data to provide the Service. You
								may disconnect at any time.
							</Typography>
						</li>
						<li>
							<Typography variant="body1">
								<b>Your responsibility for communications.</b> You are the
								sender and party of record for reminders and communications sent
								to your customers through the Service. You are responsible for
								the accuracy of your invoice data and for complying with all laws
								that apply to your collections and communications (for example,
								TCPA, CAN-SPAM, and any applicable debt-collection rules).
							</Typography>
						</li>
						<li>
							<Typography variant="body1">
								<b>No custody of funds.</b> FundsyFi Recover does not take
								custody of, hold, or escrow your customers’ payments. Payments on
								your invoices are made through your own QuickBooks and payment
								arrangements.
							</Typography>
						</li>
						<li>
							<Typography variant="body1">
								<b>AI features.</b> Certain features use AI to classify replies,
								suggest scores, and help draft messages. Outputs are estimates
								and may be inaccurate; you are responsible for reviewing them
								before relying on or sending them.
							</Typography>
						</li>
						<li>
							<Typography variant="body1">
								<b>No professional advice.</b> FundsyFi Recover does not provide
								legal, accounting, tax, or debt-collection advice.
							</Typography>
						</li>
					</Box>
				</Section>
				<Section>
					<Typography variant="h5" gutterBottom>
						18. Contact Us
					</Typography>
					<Typography variant="body1" paragraph>
						If you have any questions about these Terms, please contact us at:
					</Typography>
					<Typography variant="body1">
						<Link href="mailto:support@fundsyfi.com">support@fundsyfi.com</Link>
					</Typography>
					<Typography variant="body1">Houston, Texas 77085</Typography>
				</Section>
			</PageContainer>
			<Footer />
		</React.Fragment>
	);
};

export default TermsOfService;
